import {
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { ComposeModal } from "../../../components/dashboard/compose";
import { InboxListPage } from "../../../components/dashboard/inbox";
import { inboxSelectionFilter, parseInboxDirection } from "@/features/inbox/filters";
import { sendComposeEmail } from "@/features/inbox/server-fns";
import {
  composeOptionsQueryOptions,
  flattenInboxThreadPages,
  inboxThreadQueryKey,
  inboxThreadsInfiniteQueryOptions,
} from "@/features/inbox/queries";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { settingsUsageOverviewQueryOptions } from "@/features/settings/queries";

export const Route = createFileRoute("/dashboard/compose/")({
  validateSearch: (search: Record<string, unknown>) => ({
    domainId: (search.domainId as string) || undefined,
    aliasId: (search.aliasId as string) || undefined,
    direction: parseInboxDirection(search.direction),
    q: (search.q as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: "Compose - Bavimail" }],
  }),
  staleTime: 5_000,
  preloadStaleTime: 5_000,
  loaderDeps: ({ search }) => ({
    domainId: search.domainId,
    aliasId: search.aliasId,
    direction: search.direction,
    q: search.q,
  }),
  loader: async ({ deps, context }) => {
    await Promise.all([
      context.queryClient.ensureInfiniteQueryData(
        inboxThreadsInfiniteQueryOptions({
          domainId: deps.domainId,
          ...inboxSelectionFilter(deps.aliasId),
          direction: deps.direction,
          search: deps.q,
        }),
      ),
      context.queryClient.ensureQueryData(composeOptionsQueryOptions()),
    ]);
  },
  component: DashboardComposeRoute,
});

function DashboardComposeRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { domainId, aliasId, direction, q } = Route.useSearch();
  const { data: composeAliases } = useSuspenseQuery(composeOptionsQueryOptions());
  const usageOverviewQuery = useQuery({
    ...settingsUsageOverviewQueryOptions(),
    retry: false,
    throwOnError: false,
  });
  const threadsQuery = useSuspenseInfiniteQuery(
    inboxThreadsInfiniteQueryOptions({
      domainId,
      ...inboxSelectionFilter(aliasId),
      direction,
      search: q,
    }),
  );

  const threads = useMemo(() => flattenInboxThreadPages(threadsQuery.data), [threadsQuery.data]);
  const lastPage = threadsQuery.data.pages[threadsQuery.data.pages.length - 1];

  return (
    <>
      <InboxListPage
        threads={threads}
        selectedDomainId={domainId}
        selectedAliasId={aliasId}
        activeDirection={direction}
        searchQuery={q || ""}
        hasMore={lastPage?.hasMore || false}
        onLoadMore={() => {
          if (threadsQuery.hasNextPage) {
            void threadsQuery.fetchNextPage();
          }
        }}
        isLoadingMore={threadsQuery.isFetchingNextPage}
        isSearching={threadsQuery.isFetching && !threadsQuery.isFetchingNextPage}
      />
      <ComposeModal
        mode="compose"
        aliases={composeAliases}
        showBavimailFooter={!usageOverviewQuery.data?.subscription.isPaidPlan}
        onClose={() =>
          navigate({ to: "/dashboard/inbox", search: { domainId, aliasId, direction, q } })
        }
        onSend={async (input) => {
          const result = await sendComposeEmail({ data: input });
          capturePostHogEvent("email_sent", { mode: "compose" });
          await Promise.all([
            result.conversationId
              ? queryClient.invalidateQueries({
                  queryKey: inboxThreadQueryKey(result.conversationId),
                })
              : Promise.resolve(),
            queryClient.invalidateQueries({ queryKey: ["inboxThreads"] }),
          ]);
          if (result.conversationId) {
            await navigate({
              to: "/dashboard/inbox/$id",
              params: { id: result.conversationId },
              search: { domainId, aliasId, direction, q },
            });
            return;
          }

          await navigate({ to: "/dashboard/inbox", search: { domainId, aliasId, direction, q } });
        }}
      />
    </>
  );
}
